// FILE: delegation.ts
// Purpose: The single reading of what a managed agent's delegations are doing,
//          shared by the web (which paints it) and the server (which acts on it).
// Layer: shared runtime utility
// Depends on: nothing but the shapes below

/**
 * Role a thread carries when it is a delegation opened with
 * `agent_delegate_open`.
 *
 * Lives here because three places need to agree on it: the tool that stamps it,
 * the sidebar that groups by it, and the reactor that wakes the parent.
 */
export const DELEGATED_SUBAGENT_ROLE = "delegate";

/**
 * The minimum a thread has to expose to be judged as a delegation.
 *
 * Deliberately structural and unbranded: the server holds `OrchestrationThread`
 * and the web holds its own summaries, with `null` on one side and `undefined`
 * on the other for the same absence. Both are assignable to this.
 */
export interface DelegationThreadView {
  readonly id: string;
  readonly parentThreadId?: string | null | undefined;
  readonly subagentRole?: string | null | undefined;
  readonly archivedAt?: string | null | undefined;
  readonly session?:
    | {
        readonly status?: string | null | undefined;
        readonly activeTurnId?: string | null | undefined;
      }
    | null
    | undefined;
  readonly latestTurn?:
    | {
        /**
         * Which turn delivered the report. It is what tells one report from the
         * next when the same delegation answers round after round — see
         * `delegationWakeKey` on the server.
         */
        readonly turnId?: string | null | undefined;
        readonly startedAt?: string | null | undefined;
        readonly completedAt?: string | null | undefined;
      }
    | null
    | undefined;
  /**
   * When this delegation's parent last picked a report up, from the newest
   * `delegation.report.read` activity. See [[hasUnreadDelegateReport]].
   */
  readonly delegateReportReadAt?: string | null | undefined;
}

/**
 * A thread that is one of its parent's delegations, and still on the active list.
 *
 * Archiving is how a delegating agent closes a finished delegation
 * (`agent_delegate_close`), so an archived child is settled business: it must not
 * keep its parent looking busy, nor claim the parent still owes it a read.
 */
export function isLiveDelegateThread(thread: DelegationThreadView): boolean {
  return thread.subagentRole === DELEGATED_SUBAGENT_ROLE && thread.archivedAt == null;
}

/**
 * A thread that was opened as one of its parent's delegations, archived or not.
 *
 * The counterpart to [[isLiveDelegateThread]], and the distinction matters:
 * readiness questions ("does the parent still owe this a read?") must ignore an
 * archived delegation, but *identity* questions ("is this the user's work or the
 * agent's?") must not. Closing a delegation with `agent_delegate_close` archives
 * it, so judging identity with the live predicate would hand a closed delegation
 * back its notifications and its unread badge the moment it was settled.
 *
 * Deliberately narrower than `parentThreadId != null`: a side chat is also a
 * child thread, but the user opened it by hand and is waiting for its answer.
 */
export function isDelegateThread(thread: Pick<DelegationThreadView, "subagentRole">): boolean {
  return thread.subagentRole === DELEGATED_SUBAGENT_ROLE;
}

/**
 * Whether a delegation has a turn in flight.
 *
 * Both halves are required: a session that says "running" while holding no
 * active turn is one that was left open, not work in progress, and reporting it
 * as busy would make the parent wait forever. Same reading the server gives
 * `agent_delegate_poll` (`describeDelegateStatus`).
 */
export function isDelegateWorking(thread: DelegationThreadView): boolean {
  return thread.session?.status === "running" && Boolean(thread.session.activeTurnId);
}

/**
 * Whether a delegation has delivered something its parent has not acted on yet.
 *
 * "Delivered" is a finished turn; "acted on" is a read receipt written by
 * `agent_delegate_poll` after that turn finished. A parent still working is
 * judged the way it looks: its turn has no `completedAt`, so nothing counts as
 * unread while it runs.
 *
 * The receipt replaced an inference from clocks — "the parent's turn ended after
 * the child's, so it must have read it". That held only while a delegating agent
 * stayed awake waiting for its delegate. Now that it is told to end its turn and
 * be woken, a delegate that finishes *before* the parent's turn ends is the
 * ordinary case, and inferring a read from the clock would bury that report:
 * nothing would ever announce it again.
 *
 * A delegation that was never polled therefore counts as unread. That is what
 * makes the wake reactor pick up threads whose report predates this receipt.
 *
 * This is the predicate the sidebar paints as "Delegate Ready" *and* the one the
 * wake reactor acts on. Keeping one implementation is the point: if they ever
 * disagreed, the UI would promise a report the server never delivers.
 */
export function hasUnreadDelegateReport(child: DelegationThreadView): boolean {
  if (isDelegateWorking(child)) return false;
  const deliveredAt = child.latestTurn?.completedAt;
  if (!deliveredAt) return false;
  const delivered = Date.parse(deliveredAt);
  if (Number.isNaN(delivered)) return false;

  const readAt = child.delegateReportReadAt;
  if (!readAt) return true;
  const read = Date.parse(readAt);
  if (Number.isNaN(read)) return true;
  return read < delivered;
}

/** The direct, non-archived delegations of one thread. */
export function directDelegations<T extends DelegationThreadView>(
  threads: ReadonlyArray<T>,
  parentThreadId: string,
): ReadonlyArray<T> {
  return threads.filter(
    (thread) => thread.parentThreadId === parentThreadId && isLiveDelegateThread(thread),
  );
}
